import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { apiGet } from '@/pages/dashboard/api';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { Badge, ErrorState, Skeleton } from '@/components/primitives';
import { severityMeta, normalizeSeverity } from '@/lib/severity';
import IocList from './IocList';
import {
  briefId, cleanMarkdown, cleanText, cvssBand, extractCvss, formatUtc,
  mitigationTitle, relTime, safeHostname, splitInsight,
} from './inboxUtils';

function Section({ title, count, children, testid }) {
  return (
    <section className="ib-doc-section" data-testid={testid}>
      <h2 className="ib-doc-label">
        {title}
        {count != null && <span className="ib-doc-label-count">{count}</span>}
      </h2>
      {children}
    </section>
  );
}

function KeyFacts({ brief, cvss, sourceCount }) {
  const sev = normalizeSeverity(brief.severity);
  const band = cvssBand(cvss);
  const disagrees = cvss != null && band !== sev && sev !== 'Info';
  const cves = brief.cves || [];
  return (
    <dl className="ib-facts" data-testid="brief-key-facts">
      {cvss != null && (
        <div className="ib-fact">
          <dt>CVSS</dt>
          <dd>
            <span className="ib-mono">{cvss.toFixed(1)}</span>
            <span className="ib-fact-sub">{band}, as quoted by sources</span>
            {disagrees && (
              <span className="ib-fact-note">
                The {sev} rating is the model's assessment and differs from the quoted CVSS.
              </span>
            )}
          </dd>
        </div>
      )}
      {cves.length > 0 && (
        <div className="ib-fact ib-fact--wide">
          <dt>CVEs</dt>
          <dd className="ib-fact-chips">
            {cves.slice(0, 6).map((c) => (
              <a
                key={c}
                className="ib-cve-chip"
                href={`https://nvd.nist.gov/vuln/detail/${encodeURIComponent(c)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${c} on NVD (opens in a new tab)`}
              >
                {c}
              </a>
            ))}
            {cves.length > 6 && <span className="ib-fact-sub">+{cves.length - 6} more</span>}
          </dd>
        </div>
      )}
      <div className="ib-fact">
        <dt>Sources</dt>
        <dd className="ib-mono">{sourceCount}</dd>
      </div>
      <div className="ib-fact">
        <dt>IOCs</dt>
        <dd className="ib-mono">{brief.iocs?.length || 0}</dd>
      </div>
    </dl>
  );
}

/** The published profile for a story's actor, if one covers the name or an alias. */
function useActorProfile(names) {
  const key = names.filter(Boolean).join('|');
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    setProfile(null);
    if (!key) return undefined;
    let live = true;
    // Repeated ?name= parameters, the form FastAPI reads as a list (axios would send name[]=).
    const qs = new URLSearchParams(key.split('|').map((n) => ['name', n])).toString();
    apiGet(`/actors/lookup?${qs}`).then((d) => live && setProfile(d.profile)).catch(() => {});
    return () => { live = false; };
  }, [key]);
  return profile;
}

/**
 * Reading view for one brief. Reads like an article; the actions live in
 * the rail (desktop) or the action bar (smaller screens), not here.
 */
// The threat panel shows severity, ID, title and status in its own header, so the document starts at the byline.
const BriefDoc = function BriefDoc({ brief, rules }) {
  const bodyRef = useRef(null);
  const id = briefId(brief);

  // Each brief opens at the top of the reading pane.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [id]);

  const r = rules.status === 'ready' ? rules.data : null;
  const insightText = brief.insight || r?.insight || '';
  const insight = useMemo(() => (insightText ? splitInsight(insightText) : null), [insightText]);
  const cvss = useMemo(() => extractCvss(insightText), [insightText]);
  const sourceCount = brief.source_count || r?.source_count || brief.sources?.length || 1;
  const sources = (r?.sources?.length ? r.sources : brief.sources) || [];
  const article = r?.source_article;
  const sev = severityMeta(brief.severity);
  const actorProfile = useActorProfile([brief.threat_actor, ...(brief.threat_actor_aliases || [])]);

  // Actions are listed once: backend mitigations if there are any, else the
  // "Recommended actions" the insight carried.
  const mitigations = useMemo(() => {
    if (r?.mitigations?.length) {
      return r.mitigations.map((m) => ({ title: mitigationTitle(m), text: cleanText(m.description) }));
    }
    return (insight?.actions || []).map((t) => ({ title: '', text: t }));
  }, [r, insight]);

  return (
    <article ref={bodyRef} className="ib-doc ib-doc--embedded" aria-label="Threat brief" data-testid="brief-doc" style={{ '--sev-color': sev.color }}>

      <div className="ib-doc-byline">
        {brief.threat_actor && (
          <span>
            Attributed to <strong>{brief.threat_actor}</strong>
            {actorProfile && <> <Link className="ib-actor-link" to={`/actors/${actorProfile.slug}`} data-testid="actor-profile-link">Profile{actorProfile.name !== brief.threat_actor ? ` of ${actorProfile.name}` : ''}</Link></>}
            {brief.threat_actor_aliases?.length > 0 && (
              <span className="ib-doc-aliases"> (also {brief.threat_actor_aliases.slice(0, 3).join(', ')})</span>
            )}
          </span>
        )}
      </div>

      {brief.match_reasons?.length > 0 && (
        <p className="ib-match" data-testid="match-reasons">
          <span className="ib-match-label">Why you're seeing this</span>
          {brief.match_reasons.map((r) => <span key={r} className="ib-match-chip">{r}</span>)}
        </p>
      )}

      <KeyFacts brief={brief} cvss={cvss} sourceCount={sourceCount} />

      {insight?.body ? (
        <Section title={sourceCount > 1 ? `Unified insight · ${sourceCount} sources` : 'Insight'} testid="unified-insight">
          <MarkdownRenderer content={insight.body} className="ib-md" />
        </Section>
      ) : (
        <>
          <p className="ib-doc-lede">{cleanText(brief.description)}</p>
          {article?.original_summary && cleanText(article.original_summary) !== cleanText(brief.description) && (
            <Section title="From the source article">
              <MarkdownRenderer content={cleanMarkdown(article.original_summary)} className="ib-md" />
            </Section>
          )}
        </>
      )}

      {rules.status === 'error' && (
        <ErrorState
          variant="inline"
          title="Couldn't load detections and mitigations"
          error={rules.error}
          onRetry={rules.reload}
          className="ib-doc-alert"
        />
      )}

      {(r?.mitre_tactics?.length > 0 || brief.ttps?.length > 0) && (
        <Section title="MITRE ATT&CK">
          {r?.mitre_tactics?.length > 0 && (
            <div className="ib-chips">
              {r.mitre_tactics.map((t) => <Badge key={t} tone="accent" size="sm">{t}</Badge>)}
            </div>
          )}
          {brief.ttps?.length > 0 && (
            <ol className="ib-numbered">
              {brief.ttps.map((t, i) => <li key={i}>{cleanText(t)}</li>)}
            </ol>
          )}
        </Section>
      )}

      {brief.iocs?.length > 0 && (
        <Section title="Indicators of compromise" count={brief.iocs.length} testid="iocs-section">
          <IocList iocs={brief.iocs} attackId={id} />
        </Section>
      )}

      {rules.status === 'loading' && !insight?.actions?.length ? (
        <Section title="Recommended mitigations">
          <div className="ib-doc-skeleton" aria-hidden="true">
            <Skeleton height={14} width="80%" />
            <Skeleton height={14} width="64%" />
            <Skeleton height={14} width="72%" />
          </div>
        </Section>
      ) : mitigations.length > 0 && (
        <Section title="Recommended mitigations" count={mitigations.length} testid="mitigations">
          <ol className="ib-mitigations">
            {mitigations.map((m, i) => (
              <li key={i}>
                {m.title && <div className="ib-mitigation-title">{m.title}</div>}
                <div className="ib-mitigation-text">{m.text}</div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {sources.length > 1 ? (
        <Section title="Sources" count={sources.length} testid="story-sources">
          <ul className="ib-sources">
            {sources.map((src, i) => (
              <li key={src.url || i}>
                <a href={src.url} target="_blank" rel="noopener noreferrer" className="ib-source-link">
                  {cleanText(src.title) || src.url}
                  <ExternalLink aria-hidden="true" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
                <div className="ib-source-meta">
                  {safeHostname(src.url) || src.feed}
                  {src.published_at && <> · <time dateTime={src.published_at} title={formatUtc(src.published_at)}>{relTime(src.published_at)} ago</time></>}
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ) : brief.source_url && (
        <p className="ib-doc-source">
          Original report:{' '}
          <a href={brief.source_url} target="_blank" rel="noopener noreferrer" className="ib-source-link">
            {safeHostname(brief.source_url) || brief.source_url}
            <ExternalLink aria-hidden="true" />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </p>
      )}
    </article>
  );
};

export default BriefDoc;
